import { ErrorIcon } from '@polyai/ui/components/atoms/Icons';
import LoadingIcon from '@polyai/ui/components/atoms/LoadingIcon';
import { twMerge } from '@polyai/ui/utils/twMerge';

import { SETUP_STEPS_COPY } from './SetupSteps.uiCopy';

export type StepStatus = 'completed' | 'in_progress' | 'pending' | 'failed';

// Simple filled check circle using currentColor
export type CheckCircleIconProps = { invert?: boolean };

export const CheckCircleIcon = (props: CheckCircleIconProps) => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
    <circle cx="8" cy="8" fill="currentColor" r="8" />
    <path
      d="M11.5 5.5L7 10L4.5 7.5"
      stroke={props.invert ? 'black' : 'white'}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

export const SETUP_STEPS = [
  { id: 'analyzing', label: 'Analyzing website' },
  { id: 'retrieving', label: 'Retrieving data' },
  { id: 'training', label: 'Training agent' },
  { id: 'preparing', label: 'Preparing voice' },
  { id: 'finalizing', label: 'Finalizing agent' },
] as const;

export interface SetupStepsProps {
  completedSteps?: number;
  failed?: boolean;
}

export const getStepStatus = (
  stepIndex: number,
  completedSteps: number,
  failed: boolean,
): StepStatus => {
  if (failed && stepIndex === completedSteps) {
    return 'failed';
  }

  if (stepIndex < completedSteps) {
    return 'completed';
  }
  if (stepIndex === completedSteps) {
    return 'in_progress';
  }
  return 'pending';
};

const statusStyles: Record<
  StepStatus,
  { className?: string; style?: React.CSSProperties }
> = {
  completed: { className: 'text-icon-success' },
  in_progress: { className: 'text-primary-inverse' },
  pending: { className: '', style: { color: 'rgba(255, 255, 255, 0.4)' } },
  failed: { className: 'text-primary-inverse' },
};

const SetupSteps = ({
  completedSteps = 0,
  failed = false,
}: SetupStepsProps) => {
  const isComplete = completedSteps >= SETUP_STEPS.length;
  const message = isComplete
    ? SETUP_STEPS_COPY.completeMessage
    : SETUP_STEPS_COPY.buildingMessage;

  return (
    <div
      className="relative z-sticky flex flex-col gap-sm1 max-w-[320px] mb-[5%] p-sm2"
      data-test-id="agent-wizard-setup-steps"
    >
      <p className="m-0 text-header-regular leading-normal text-primary-inverse">
        {message}
      </p>

      <div className="flex flex-col gap-sm1">
        {SETUP_STEPS.map((step, index) => {
          const status = getStepStatus(index, completedSteps, failed);
          const { className: statusClassName, style: statusStyle } =
            statusStyles[status];
          return (
            <div
              key={step.id}
              className={twMerge(
                'flex gap-xs2 items-center',
                'text-header-regular font-semibold leading-tight',
                'transition-colors duration-200',
                statusClassName,
              )}
              data-test-id={`agent-wizard-setup-step-${step.id}`}
              style={statusStyle}
            >
              <span className="flex-1">{step.label}</span>
              <span className="flex shrink-0 items-center justify-center w-[20px] h-[20px] text-inherit">
                {status === 'completed' && <CheckCircleIcon invert />}
                {status === 'in_progress' && <LoadingIcon size={16} />}
                {status === 'failed' && <ErrorIcon size="md" />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SetupSteps;
